/*
 * Copyright (c) 2021 Omnicell. All rights reserved.
 *
 * This program contains proprietary, trade secret information of
 * Omnicell. and may not be copied, reproduced or reverse engineered
 * in any way without the prior written permission of Omnicell.
 */

import Color from 'color';
import { default as React, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
// https://github.com/Wolox/react-chat-widget
import { addResponseMessage, addUserMessage, toggleWidget, Widget } from 'react-chat-widget';
import { createPortal } from 'react-dom';
import botAvatar from '../assets/ellie.svg';
import {
  primaryBrandColorVarName,
  primaryBrandFontColorVarName,
  secondaryBrandColorVarName,
  tertiaryBrandColorVarName
} from '../constants/brandColorVariables';
import { ESCAPE_KEY, ESCAPE_KEYCODE } from '../constants/keyCodes';
import { START, TEXT } from '../constants/messageTypes';
import { BrandContext } from '../contexts/BrandContext';
import { DataContext } from '../contexts/DataContext';
import { useWindowHeight } from '../hooks/UseWindowHeight';
import { handleBotWidgetA11y, trapChatbotFocus } from '../utils/a11y-helpers';
import { registerInitialLoadCallback, sendMessageToBot } from '../utils/messaging-helpers';
import CustomLauncher from './CustomLauncher';
import Spinner from './Spinner';
import Terms from './Terms';

const ChatBot = props => {
  const windowHeight = useWindowHeight();
  const { invalidBotId, sessionId, searchParams, botId } = props;
  const { brand, logo } = useContext(BrandContext);
  const { setCurrentFlowIdentifier } = useContext(DataContext);
  const [footerContainer, setFooterContainer] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [isInitialMessageLoading, setIsInitialMessageLoading] = useState(false);
  const [conversationContainer, setConversationContainer] = useState(null);
  const widgetRef = useRef(null);
  let primaryColor;
  let secondaryColor;
  let tertiaryColor;
  let fontColor;

  useEffect(() => {
    if (invalidBotId) {
      addResponseMessage('The provided bot ID is invalid.');
      return;
    }

    // Register callback to connect WebSocket and hide spinner when first message arrives
    registerInitialLoadCallback(() => {
      setIsInitialMessageLoading(false);
    });

    // Show spinner before sending START message
    setIsInitialMessageLoading(true);

    sendMessageToBot(
      START,
      botId,
      setCurrentFlowIdentifier,
      sessionId,
      botId,
      brand.botType,
      searchParams,
      brand.messagingEndpoint,
      brand.websocketEndpoint
    );
  }, []);

  useEffect(() => {
    if (isInitialMessageLoading && widgetRef.current) {
      const container = widgetRef.current.querySelector('#rcw-conversation-container');
      setConversationContainer(container);
    } else {
      setConversationContainer(null);
    }
  }, [isInitialMessageLoading]);

  // Watch for loader appearing and scroll to it (for all loading states, not just initial)
  useEffect(() => {
    if (!widgetRef.current) return;

    const messagesContainer = widgetRef.current.querySelector('#messages');
    if (!messagesContainer) return;

    const observer = new MutationObserver(() => {
      const loader = messagesContainer.querySelector('.loader.active');
      if (loader) {
        // Scroll to bottom to show loading dots
        loader.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });

    observer.observe(messagesContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const sender = document.querySelector('.rcw-sender');
      if (sender && !document.querySelector('.rcw-custom-footer')) {
        const footer = document.createElement('div');
        footer.className = 'rcw-custom-footer';
        sender.parentNode.insertBefore(footer, sender.nextSibling);
        setFooterContainer(footer);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  useLayoutEffect(() => {
    handleBotWidgetA11y(logo, isOpen);
    if (isOpen) {
      setTheme();
    }
  }, [isOpen]);

  // Handle quick button accessibility using MutationObserver
  useEffect(() => {
    if (!widgetRef.current) return;

    const setQuickButtonA11yInfo = quickButtonsContainer => {
      const quickButtons = quickButtonsContainer.querySelectorAll('.quick-button');
      quickButtons.forEach(btn => {
        btn.setAttribute('aria-label', btn.innerText);
      });
    };

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            // Check if quick buttons container was added
            const quickButtonsContainer = node.classList?.contains('quick-buttons-container')
              ? node
              : node.querySelector?.('.quick-buttons-container');

            if (quickButtonsContainer) {
              setQuickButtonA11yInfo(quickButtonsContainer);
              trapChatbotFocus();
            }
          }
        });
      });
    });

    observer.observe(widgetRef.current, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [isOpen]);

  const calculateBrandThemeColors = () => {
    primaryColor = Color(brand.primaryColor);
    fontColor = Color(brand.fontColor);
    if (primaryColor.isLight()) {
      secondaryColor = primaryColor.darken(0.5);
      tertiaryColor = secondaryColor.darken(0.5);
      if (tertiaryColor.isDark() && fontColor.isDark()) {
        tertiaryColor = primaryColor.lighten(0.5);
      }
    } else {
      secondaryColor = primaryColor.lighten(0.5);
      tertiaryColor = secondaryColor.lighten(0.5);
      if (tertiaryColor.isLight() && fontColor.isLight()) {
        tertiaryColor = primaryColor.darken(0.5);
      }
    }
    if (tertiaryColor.luminosity() > 0.8) {
      tertiaryColor = Color('gray');
    }
  };

  const handleKeyDown = e => {
    const isEscapePressed = e.key === ESCAPE_KEY || e.keyCode === ESCAPE_KEYCODE;
    if (isEscapePressed && isOpen) {
      toggleWidget();
      handleToggleClicked(false);
    }
  };

  const handleQuickButtonClicked = buttonValue => {
    addUserMessage(buttonValue);
    sendMessageToBot(TEXT, buttonValue);
  };

  const handleToggleClicked = open => {
    setIsOpen(open);
  };

  const setTheme = () => {
    calculateBrandThemeColors();
    // set root variables for theme to the chatbot config & derived colors
    document.documentElement.style.setProperty(primaryBrandColorVarName, primaryColor.hex());
    document.documentElement.style.setProperty(primaryBrandFontColorVarName, fontColor.hex());
    document.documentElement.style.setProperty(secondaryBrandColorVarName, secondaryColor.hex());
    document.documentElement.style.setProperty(tertiaryBrandColorVarName, tertiaryColor.hex());
  };

  const renderCustomLauncher = toggle =>
    isOpen ? null : <CustomLauncher handleToggle={toggle} key="launch-btn" />;

  return (
    <div onKeyDown={handleKeyDown} ref={widgetRef}>
      <div className="widget-container">
        <Widget
          title={brand.title}
          titleAvatar={logo}
          subtitle=""
          profileAvatar={botAvatar}
          autofocus={false}
          showTimeStamp={false}
          handleQuickButtonClicked={handleQuickButtonClicked}
          handleToggle={handleToggleClicked}
          launcher={handleToggle => renderCustomLauncher(handleToggle)}
          customStyle={{
            '--dynamic-height': `${windowHeight - 122}px`
          }}
        />
        {footerContainer && createPortal(<Terms />, footerContainer)}
        {isInitialMessageLoading &&
          conversationContainer &&
          createPortal(<Spinner />, conversationContainer)}
      </div>
    </div>
  );
};

export default ChatBot;
